import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthorized } from '../../selectors/user';
import { selectFinancePlatformEnabled } from '../../selectors/landing';
import { setAuthorizedModal } from '../../redux/landingSlice';
import { useTracking } from '../../utils/tracking/useTracking';
import {
  ETargetElementForAnalytics,
  TRedirectForAnalytics,
  TTargetElementForAnalytics,
} from '../../utils/tracking/types';
import { EFillApplicationStickyButtonType } from '../../containers/stickyButtons/FillApplicationButton/types';

export interface IAuthorizeAndRedirectProps extends IOfferBtnProps {
  authorizationRequired?: boolean;
  path: string;
}

interface IOfferBtnProps {
  bankName?: string | null;
  place?: number;
  isMultiOffer?: boolean;
  analyticEventKey: TRedirectForAnalytics;
  targetElementForAnalytics?: TTargetElementForAnalytics;
  text?: string;
}

export const useAuthorized = () => {
  const dispatch = useDispatch();
  const isAuthorized = useSelector(selectIsAuthorized);
  const financePlatformEnabled = useSelector(selectFinancePlatformEnabled);
  const { trackRedirect, trackClickOnMasterButton, trackClickOnModalFillApplicationButton } = useTracking();

  const trackClick = ({ bankName, place, isMultiOffer, analyticEventKey }: IOfferBtnProps) => {
    const isMasterButton =
      analyticEventKey === EFillApplicationStickyButtonType.BOTTOM ||
      analyticEventKey === EFillApplicationStickyButtonType.TOP;

    if (isMasterButton) {
      trackClickOnMasterButton(analyticEventKey);
    } else {
      trackClickOnModalFillApplicationButton({
        place: Number(place),
        bank: bankName as string,
        isMultiOffer: Boolean(isMultiOffer),
        type: analyticEventKey,
      });
    }
  };

  const authorizeAndRedirect = ({
    authorizationRequired = true,
    path,
    bankName,
    place,
    analyticEventKey,
    isMultiOffer,
    targetElementForAnalytics = ETargetElementForAnalytics.BUTTON,
  }: IAuthorizeAndRedirectProps) => {
    trackClick({ bankName, place, isMultiOffer, analyticEventKey, targetElementForAnalytics });

    const shouldRequireAuth = authorizationRequired && !financePlatformEnabled;

    if (!isAuthorized && shouldRequireAuth) {
      dispatch(
        setAuthorizedModal({
          bankName: bankName as string,
          isOpen: true,
          pathForRedirect: path,
          typeForRedirect: analyticEventKey,
          targetElementForRedirect: targetElementForAnalytics,
        }),
      );

      return;
    }

    trackRedirect(analyticEventKey, targetElementForAnalytics, bankName);
    window.open(path, '_self');
  };

  return { isAuthorized, authorizeAndRedirect };
};
